import { isDate, isObject, isNull, isString, isEmpty, unionBy, isArray } from 'lodash';
import qs from 'qs';
import dayjs from 'dayjs';
import { formatNumberByThousands } from '@ma/cube';
import Config from '../api/config';
import { getGhostUser } from '../api/debug';

/**
 * 是否调试模式
 * @return {Boolean}
 */
export function isDebug() {
  return ['development', 'test'].indexOf(process.env.NODE_ENV) !== -1;
}

/**
 * 是否站点域名地址
 * @return {Boolean}
 */
export function isProjectUrl(url) {
  if (!url) {
    return false;
  }

  // 相对路径都认为是true
  if (!isHttpURL(url)) {
    return true;
  }

  return url.indexOf(window.location.origin) === 0;
}

/**
 * 是否http(s) url
 * @param {*} url
 * @return {Boolean}
 */
export function isHttpURL(url) {
  return /^https?:\/\//i.test(url);
}

/**
 * 转换为站点完整的url
 * @param {String} paramsUrl
 * @param {Boolean} [baseAppStatic:false] 是否基于App静态资源路径, 只对App平台有效
 * eg:
 *  1) tpptest.oa.com/app/xxx
 *  2) oa.m.tencent.com/an:tpptest/app/xxx
 * @return {String}
 */
export function toBaseURL(paramsUrl, baseAppStatic = false) {
  let url = paramsUrl;
  if (!url) {
    return url;
  }

  // 开发测试环境，附加ghostlogin参数，兼容客户端本地调试时访问文件等的鉴权
  if (isDebug() && isProjectUrl(url)) {
    const user = getGhostUser();
    const tmpUrl = url.split('?');
    let parsed = null;

    if (tmpUrl[1]) {
      parsed = qs.parse(tmpUrl[1], { ignoreQueryPrefix: true });
      if (!parsed.ghostlogin) {
        parsed.ghostlogin = user;
      }
    } else {
      parsed = {
        ghostlogin: user,
      };
    }

    url = `${tmpUrl[0]}?${qs.stringify(parsed)}`;
  }

  if (isHttpURL(url)) {
    return url;
  }

  // 对于App, url可能是本地的静态资源，此时已经带上根路径
  // 对于移动网关的情况，会带上'/an:tpptest'的路径，此时再附加baseURL会有问题
  // eg: url = /an:tpptest/app/assets/images/xxx.png
  // 需要转为/app/assets/images/xxx.png
  if (process.env.PLATFORM === 'app') {
    const rootpath = window.__public_root_path;
    if (url.indexOf(rootpath) !== -1) {
      url = url.replace(rootpath, '');
    }
  }

  let { baseURL } = Config;

  // App下，非开发模式下需要附加根目录
  // https://tpptest.oa.com/app
  // https://oa.m.tencent.com/an:tpptest/app
  if (process.env.PLATFORM === 'app' && baseAppStatic) {
    const path = `${baseURL}/app`;
    // 检查当前路径是否包含/app目录来判断
    if (window.location.href.toLowerCase().indexOf(path.toLowerCase()) === 0) {
      baseURL += '/app';
    }
  }

  return baseURL + (url.substr(0, 1) === '/' ? url : `/${url}`);
}

/**
 * 判断是否为File实例
 * @param {*} file
 */
function isFile(file) {
  return file instanceof File;
  // return isBlob(value) &&
  //  (typeof value.lastModifiedDate === 'object' || typeof value.lastModified === 'number') &&
  //  typeof value.name === 'string'
}

/**
 * 将一个对象的属性层级打平，多级变为一级，主要用于表单提交使用
 * @param {*} obj
 * @param {function} determine 用于决定一个对象是否要更深一步去打平，返回true表示要更深一层，返回false则直接将该对象作为键值
 */
export function flatObject(obj, determine) {
  const flat = (input, path = '', obj = {}) => {
    const result = obj;
    if (Array.isArray(input)) {
      input.forEach((item, i) => flat(item, `${path}[${i}]`, result));
      return result;
    }
    if (input && typeof input === 'object' && !isFile(input) && !isDate(input)) {
      if (typeof determine === 'function' && !determine(input)) {
        result[path] = input;
        return result;
      }

      const keys = Object.keys(input);
      keys.forEach((key) => {
        const value = input[key];
        flat(value, !path ? key : `${path}[${key}]`, result);
      });
      return result;
    }

    result[path] = input;
    return result;
  };
  if (!obj || typeof obj !== 'object') {
    return {};
  }
  return flat(obj);
}

export const path2Params = (path) => {
  let str = path;
  if (isObject(str)) return str;
  if (str.indexOf('?') === 0) {
    str = str.slice(1);
  }
  const obj = {};
  str.split('&').forEach((item) => {
    const [key, val] = item.split('=');
    !isEmpty(key) && (obj[key] = decodeURIComponent(val));
  });
  return obj;
};

export const params2Path = (obj) =>
  Object.entries(obj).reduce((acc, [key, val], index) => {
    let result = acc;
    return val ? (result += `${index !== 0 ? '&' : ''}${key}=${val}`) : result;
  }, '?');

export const generateSearchParams = (location, newParams) => ({
  ...location,
  search: params2Path({
    ...path2Params(location.search),
    ...newParams,
  }),
});

/**
 * options
 *  {
 *    replace
 *  }
 */
export const generateHistoryPurePush =
  (history) =>
  (newParams, { replace } = {}) => {
    const newSearch = params2Path({
      ...path2Params(replace ? {} : window.location.search),
      ...path2Params(newParams),
    });
    if (history.location.search === newSearch) return;
    history.push({
      ...history.location,
      search: newSearch,
    });
  };
/**
 * 过滤行业
 */
export function professionsFilter(professions) {
  const filterProfessionNameList = [
    'O2O',
    'Fintech',
    'Healthcare',
    'Education',
    'Electric Vehicle',
    'Entertainment',
    'E-commerce',
    'Logistics',
    'Enterprise Services',
  ];
  const filterProfessionIdList = [
    '04E47310D401B8E32346B9FBB7F0EB8D',
    '1203C629EDC140AA6DEF67121D5BA05E',
    '26B826FFFDEAD1192BB2D247734BECED',
    '63075DD28C823E59E8D9FBD29BB45453',
    '7B07FDA2F3ADD6B213C9B52B774603E7',
    'A4F846C95BB1DF8357ECE0A8284A6632',
    'AA9667B74BC59BF45D1BF4A431B0FA8F',
    'F133E4F83C47E39CDD328E647A81FD88',
    'FA66FF13F749DA38B6095730557794E1',
  ];

  return professions.filter(
    (item) =>
      filterProfessionNameList.includes(item.profession_name_en) || filterProfessionIdList.includes(item.profession_id),
  );
}

/**
 * 将props的页数转为接口需要参数
 * @param {*} pagination
 */
export const translatePagination2Params = (pagination) => {
  const { currentPage, pageSize } = pagination;
  return {
    page: currentPage,
    per_page: pageSize,
  };
};

export function unionByColumn(columns) {
  return unionBy(columns, (col, index) => {
    if (isString(col.Header)) return col.Header;
    if (!isEmpty(col.id)) return col.id;
    return index;
  });
}

// 行列反转
export function tableRowReverse({ data, columnKey, keys, columnFormat, formatRow, formatCell, initColumn = [] }) {
  const tableData = [];
  const columns = initColumn;

  data.forEach((item, colIndex) => {
    const dataKey = item[columnKey];
    let column = {
      Header: String(dataKey),
      accessor: String(dataKey),
    };
    if (columnFormat) {
      column = columnFormat(column, colIndex);
    }
    columns.push(column);
  });

  keys.forEach((key) => {
    let dataItem = data
      .map((dataItem) => {
        let item = {
          key: dataItem[columnKey],
          value: dataItem[key],
        };
        if (formatCell) {
          item = formatCell({
            code: key,
            ...item,
          });
        }
        return item;
      })
      .reduce((acc, cur) => {
        acc[cur.key] = cur.value;
        return acc;
      }, {});
    if (formatRow) {
      dataItem = formatRow(dataItem, key);
    }
    tableData.push(dataItem);
  });

  const unionColumn = unionByColumn(columns);

  return [tableData, unionColumn];
}

export function filterDisplayData(data, format) {
  let value = data;
  if (isNull(value) || (isString(value) && !value)) {
    return '-';
  }
  if (format === undefined) {
    return value;
  }

  const type = format.split(':');
  let showBooleanMap = [];

  switch (type[0]) {
    case 'text':
      return value;
    case 'concat':
      return value.join(type[1]);
    case 'boolean':
      showBooleanMap = type[1].split(',');
      if (value === 0) {
        return showBooleanMap[1];
      }
      return showBooleanMap[0];
    case 'percent':
      if (typeof value === 'string' && value.indexOf('%') > 0) {
        return value;
      }
      value = parseFloat(value);
      value = value * 100;
      value = `${value.toFixed(type[1])}%`;
      return value;
    case 'money':
      value = parseFloat(value).toFixed(type[1]);
      value = scientificNotationToString(value);
      value = formatNumberByThousands(value);
      return value;
    case 'number':
      value = parseFloat(value).toFixed(type[1]);
      value = formatNumberByThousands(value);
      return value;
    case 'date':
      return dayjs(value).format('YYYY-MM-DD');
    case 'datetime':
      return dayjs(value).format('YYYY-MM-DD HH:mm');
    default:
      return value;
  }
}

// 处理科学计数法大数
export function scientificNotationToString() {
  return (value) => {
    const str = String(value);
    const flag = /e/.test(str); // 是否科学计数法
    if (!flag) {
      return value;
    }
    const index = Number(str.match(/\d+$/)[0]); // 指数
    const basis = str.match(/^[\d.]+/)[0].replace(/\./, ''); // 基数
    return basis.padEnd(index + 1, 0);
  };
}

export function getLastWeek(i = 1) {
  const startDay = dayjs().subtract(i, 'week').startOf('week').format('YYYY-MM-DD'); // 周一日期
  const endDay = dayjs().subtract(i, 'week').endOf('week').format('YYYY-MM-DD'); // 周日日期
  return [startDay, endDay];
}
export function getLastMonth(i = 1) {
  const startDay = dayjs().subtract(i, 'month').startOf('month').format('YYYY-MM-DD'); // 周一日期
  const endDay = dayjs().subtract(i, 'month').endOf('month').format('YYYY-MM-DD'); // 周日日期
  return [startDay, endDay];
}

/**
 * 复制内容到粘贴板
 * content : 需要复制的内容
 * callback : 成功的回调
 */
export function copyToClip(content, callback) {
  const aux = document.createElement('input');
  aux.setAttribute('value', content);
  document.body.appendChild(aux);
  aux.select();
  document.execCommand('copy');
  document.body.removeChild(aux);
  callback?.();
}

export function isValueEmpty(value) {
  if (value === null || value === undefined || value === '' || (typeof value === 'number' && isNaN(value))) {
    return true;
  }
  if (isArray(value)) {
    return value.length === 0;
  }
  if (isObject(value)) {
    return (0, _keys.default)(value).length === 0;
  }

  return false;
}
